import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { switchMap } from 'rxjs/operators';
import { WizardOption } from 'src/app/models/wizard/wizard-option';
import { WizardStep } from 'src/app/models/wizard/wizard-step';
import { AnalyticsService, AnalyticsUtility } from 'src/app/services/analytics/analytics.service';
import { ConfigService } from 'src/app/services/config/config.service';
import { WizardStepName } from 'src/app/services/wizard/wizard-step-name.enum';
import { WizardService } from 'src/app/services/wizard/wizard.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-use-case-step',
  templateUrl: './use-case-step.component.html',
  styleUrls: ['./use-case-step.component.scss']
})
export class UseCaseStepComponent implements OnInit {
  title: string = "";
  subtitleBeginning: string = "";
  subtitleMiddle: string = "";
  subtitleEnd: string = "";
  i18nSubtitle: string = "";
  isEnglish: boolean = true;
  isContinueBtnDisabled: boolean = true;
  currentStep: WizardStep = new WizardStep();
  stepNumber: number = 0;
  useCaseOptions: WizardOption[] = [];
  isEducation: boolean = false;
  analyticsStepName: string = AnalyticsUtility.wizardStepName.useCase;

  constructor(
    public wizardService: WizardService,
    private route: ActivatedRoute,
    private confSvc: ConfigService,
    private analyticsService: AnalyticsService,
    private utilityService: UtilityService) { }

  async ngOnInit(): Promise<void> {
    await this.confSvc.setup();

    this.currentStep = this.wizardService.getStep(WizardStepName.UseCase);
    this.stepNumber = this.wizardService.getStepNumber(this.currentStep.name);
    this.title = this.confSvc.getString("useCaseTitle");
    this.subtitleBeginning = this.confSvc.getString("wizardStepSubtitleBeginning");
    this.subtitleMiddle = this.confSvc.getString("singleSelectionWizardStepSubtitleMiddle");
    this.subtitleEnd = this.confSvc.getString("singleSelectionWizardStepSubtitleEnd");
    this.i18nSubtitle = this.confSvc.getString("i18nSingleSelectionWizardStepSubtitle");

    this.isEnglish = this.utilityService.getLocaleInfo().isEnglish;

    this.route.queryParams.subscribe(params => {
      this.wizardService.setSelectedOptionsFromUrlIfPresent(params);

      this.setUseCaseOptions();
    });
    this.analyticsService.logCurrentPage(this.analyticsStepName);
  }

  setUseCaseOptions(): void {
    this.wizardService.getSelectedWizardOptionsForStep(WizardStepName.PrimaryUse)
      .pipe(
        switchMap((primaryUseOptions: WizardOption[]) => {
          const primaryUseTitle = primaryUseOptions[0]?.title ? primaryUseOptions[0].title : "";
          this.isEducation = primaryUseTitle === this.confSvc.getString("primaryUseEducationTitle");

          return this.wizardService.getSelectedWizardOptionsForStep(this.currentStep.name);
        })
      )
      .subscribe((useCaseOptions: WizardOption[]) => {
        this.isContinueBtnDisabled = useCaseOptions.length === 0;

        if (this.isEducation) {
          this.buildEducationUseCaseOptions(useCaseOptions)
        } else {
          this.buildEnterpriseUseCaseOptions(useCaseOptions)
        }
      });
  }

  buildEducationUseCaseOptions(selectedOptions: WizardOption[]) {
    if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseGradeSchoolTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayEdu"))) {
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseGradeSchoolTitle"),
        subtitle: this.confSvc.getString("useCaseGradeSchoolSubtitle"),
        iconFontSet: 'fas',
        iconName: 'fa-child',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseGradeSchoolTitle")),
      }));
    }

    if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseHighSchoolCollegeTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayEdu"))) {
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseHighSchoolCollegeTitle"),
        subtitle: this.confSvc.getString("useCaseHighSchoolCollegeSubtitle"),
        iconFontSet: 'fas',
        iconName: 'fa-user-graduate',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseHighSchoolCollegeTitle")),
      }));
    }

    if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseTeachersStaffTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayEdu"))) {
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseTeachersStaffTitle"),
        subtitle: this.confSvc.getString("useCaseTeachersStaffSubtitle"),
        iconFontSet: 'fas',
        iconName: 'fa-chalkboard-teacher',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseTeachersStaffTitle")),
      }));
    }
  }

  buildEnterpriseUseCaseOptions(selectedOptions: WizardOption[]) {
    if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseFrontlineWorkerTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayEnterprise"))) {
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseFrontlineWorkerTitle"),
        subtitle: this.confSvc.getString("useCaseFrontlineWorkerSubtitle"),
        iconFontSet: 'fas',
        iconName: 'fa-users',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseFrontlineWorkerTitle")),
        tooltipText: this.confSvc.getString("useCaseFrontlineWorkerTooltip")
      }));
    }
    
    if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseKioskTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayEnterprise"))) {
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseKioskTitle"),
        subtitle: this.confSvc.getString("useCaseKioskSubtitle"),
        iconSvgName: 'icon_kiosk',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseKioskTitle")),
        tooltipText: this.confSvc.getString("useCaseKioskTooltip")
      }));
    }

    if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseContactCenterTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayEnterprise"))) {
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseContactCenterTitle"),
        subtitle: this.confSvc.getString("useCaseContactCenterSubtitle"),
        iconFontSet: 'fas',
        iconName: 'fa-headset',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseContactCenterTitle")),
        tooltipText: this.confSvc.getString("useCaseContactCenterTooltip")
      }));
    }

    if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseInformationWorkerTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayEnterprise"))) {
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseInformationWorkerTitle"),
        subtitle: this.confSvc.getString("useCaseInformationWorkerSubtitle"),
        iconFontSet: 'fas',
        iconName: 'fa-user-cog',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseInformationWorkerTitle")),
        tooltipText: this.confSvc.getString("useCaseInformationWorkerTooltip")
      }));
    }

    if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("useCaseExecutiveWorkerTitle"), this.confSvc.getStringArray("useCaseOptionsToDisplayEnterprise"))) {
      this.useCaseOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("useCaseExecutiveWorkerTitle"),
        subtitle: this.confSvc.getString("useCaseExecutiveWorkerSubtitle"),
        iconFontSet: 'fas',
        iconName: 'fa-user-tie',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("useCaseExecutiveWorkerTitle")),
        tooltipText: this.confSvc.getString("useCaseExecutiveWorkerTooltip")
      }));
    }

    
  }

  onSelect(event: WizardOption): void {
    this.isContinueBtnDisabled = !event.isSelected;
    this.useCaseOptions.map(item => {
      if (item.title !== event.title) {
        item.isSelected = false;
        this.wizardService.removeSelectedWizardOption(item.title);
      } else {
        item.isSelected = event.isSelected;
      }
    });
    if (event.isSelected) {
      this.wizardService.addSelectedWizardOption(event);
    }
  }
}
